<template>
    <app-card hide-header>
        <div class="">
            <v-data-table-server
                :headers="headers"
                :items="pagedResults"
                :items-per-page="tableSettings.rowsPerPage"
                v-model:options="options"
                :items-length="totalItems"
                :loading="loading"
                :page="tableSettings.pageNumber"
                :hide-default-footer="true"
                class="profiles-users-table"
                id="profiles-users-table"
                @click:row="(event, { item }) => onRowClick(item)"
            >
                <template v-slot:top>
                    <table-options-row>
                        <template #search>
                            <div class="pa-3" style="display: flex; align-items: center; width: 100%">
                                <v-icon class="ml-2 mr-2">mdi-magnify</v-icon>
                                <input
                                    v-model="tableSettings.searchString"
                                    placeholder="Search"
                                    style="flex: 1; outline: none; border: none; background: transparent"
                                >
                            </div>
                        </template>
                    </table-options-row>
                </template>

                <template v-slot:no-data>
                    <div class="no-data">There are no profiles or users that match your search</div>
                </template>

                <template v-slot:item.type="{ item }">
                    <div style="display: flex; align-items: center">
                        <app-chip
                            v-if="item.type === 1"
                            color="blue"
                            label
                            class="ml-2"
                        >
                            {{ getType(item.type) }}
                        </app-chip>
                        <app-chip
                            v-if="item.type === 4"
                            color="green"
                            label
                            class="ml-2"
                        >
                            {{ getAccountType(item.accountTypeId) }}
                        </app-chip>
                    </div>
                </template>

                <template v-slot:item.email="{ item }">
                    {{ getEmail(item) }}
                </template>

                <template v-slot:bottom>
                    <table-footer
                        :pagination="paginationInfo"
                        @previousPage="previousPage()"
                        @nextPage="nextPage()"
                        @setPage="setPage($event)"
                        @setItemsPerPage="setItemsPerPage($event)"
                    ></table-footer>
                </template>
            </v-data-table-server>
            <div v-if="searchResults.length === 0 && !loading" class="no-data">
                No profiles or users match your search criteria
            </div>
        </div>
    </app-card>
</template>

<script>
import {masterSearchSuperAdmin} from "@/services/organization.service";
import TableFooter from "@/components/tables/TableFooter";
import TableOptionsRow from "@/components/tables/TableOptionsRow";
import moment from 'moment'

export default {
    name: "SuperAdminProfileUserTable",
    components: {
        TableFooter,
        TableOptionsRow,
    },
    data() {
        return {
            totalItems: 0,
            tableSettings: {
                pageNumber: 1,
                rowsPerPage: 10,
                sortByColumn: '',
                sortAscending: false,
                searchString: '',
            },
            searchResults: [],
            loading: false,
            options: {},
            headers: [
                { title: 'Type', key: 'type' },
                { title: 'AccountId', align: 'start', sortable: false, key: 'accountId' },
                { title: 'ProfileId', align: 'start', sortable: false, key: 'profileId' },
                { title: 'Name', key: 'title' },
                { title: 'Email', key: 'email' },
                { title: 'Organization Id', key: 'organizationId' },
                { title: 'Organization Name', key: 'organizationName' },
            ],
            typingTimer: null,
            correlationId: self.crypto.randomUUID(),
        };
    },
    mounted() {
        this.init();
    },
    watch: {
        // Debounced search on actual model changes only — replaces the @input listener
        // which fired spuriously when the data-table re-rendered its top slot.
        'tableSettings.searchString'() {
            clearTimeout(this.typingTimer);
            this.typingTimer = setTimeout(() => {
                this.tableSettings.pageNumber = 1;
                this.getDataFromApi();
            }, 300);
        },
    },
    computed: {
        paginationInfo() {
            const page = this.tableSettings.pageNumber;
            const itemsPerPage = this.tableSettings.rowsPerPage;
            const itemsLength = this.totalItems;
            const pageCount = Math.max(1, Math.ceil(itemsLength / itemsPerPage));
            const pageStart = (page - 1) * itemsPerPage;
            const pageStop = Math.min(page * itemsPerPage, itemsLength);
            return { page, itemsPerPage, itemsLength, pageCount, pageStart, pageStop };
        },
        // The masterSearch endpoint returns the full result set in a single call,
        // so we paginate it locally for display.
        pagedResults() {
            const start = (this.tableSettings.pageNumber - 1) * this.tableSettings.rowsPerPage;
            const end = start + this.tableSettings.rowsPerPage;
            return this.searchResults.slice(start, end);
        },
    },
    methods: {
        init() {
            this.tableSettings.pageNumber = parseInt(this.$route.query.PageNumber ?? '') || 1;
            this.tableSettings.rowsPerPage = parseInt(this.$route.query.RowsPerPage ?? '') || 10;
            this.tableSettings.searchString = this.$route.query.SearchString ?? '';
            this.options.page = this.tableSettings.pageNumber;
            this.options.itemsPerPage = this.tableSettings.rowsPerPage;
            this.getDataFromApi();
        },
        getDisplayDate(value) {
            return moment(value).format('MM-DD-YYYY');
        },
        getType(value) {
            if (value === 1) return 'Profile';
            if (value === 4) return 'User';
            return 'Unknown';
        },
        getAccountType(value) {
            if (value === 1) return 'User';
            if (value === 4) return 'Covid';
            if (value === 5) return 'Patient';
            return 'Unknown';
        },
        getEmail(item) {
            if (item.type === 1) {
                if (item.email) return item.email + ' (Covid)';
                return '';
            }
            if (item.type === 4) return item.email;
            return 'Unknown';
        },
        async onRowClick(item) {
            if (item.type === 1) {
                window.open(`${window.location.origin}/profiles/${item.profileId}/overview?orgId=${item.organizationId}`, '_blank');
            } else if (item.type === 4) {
                if (item.accountTypeId === 4 || item.accountTypeId === 5) {
                    window.open(`${window.location.origin}/profiles/${item.profileId}/overview?orgId=${item.organizationId}`, '_blank');
                } else {
                    window.open(`${window.location.origin}/users/${item.accountId}?orgId=${item.organizationId}`, '_blank');
                }
            }
        },
        previousPage() {
            if (this.tableSettings.pageNumber <= 1) return;
            this.tableSettings.pageNumber--;
            this.syncRouteQuery();
        },
        nextPage() {
            if (this.tableSettings.pageNumber >= this.paginationInfo.pageCount) return;
            this.tableSettings.pageNumber++;
            this.syncRouteQuery();
        },
        setPage(page) {
            this.tableSettings.pageNumber = page;
            this.syncRouteQuery();
        },
        setItemsPerPage(value) {
            this.tableSettings.rowsPerPage = value;
            this.syncRouteQuery();
        },
        syncRouteQuery() {
            // Keep existing query (orgId etc.) and update the table state params.
            const request = {
                ...this.$route.query,
                PageNumber: this.tableSettings.pageNumber,
                RowsPerPage: this.tableSettings.rowsPerPage,
                SearchString: this.tableSettings.searchString,
            };
            this.$router.push({ path: this.$route.path, query: request });
        },
        async getDataFromApi() {
            this.loading = true;
            this.correlationId = self.crypto.randomUUID();
            const myCorrelationId = this.correlationId;

            const response = await masterSearchSuperAdmin(this.tableSettings.searchString);

            // Race-condition guard: ignore stale responses if a newer one is in flight.
            if (myCorrelationId !== this.correlationId) return;

            this.loading = false;

            if (!response || !response.searchResults) {
                this.searchResults = [];
                this.totalItems = 0;
                return;
            }
            this.searchResults = response.searchResults;
            this.totalItems = response.searchResults.length;
            this.syncRouteQuery();
        },
    },
};
</script>

<style scoped>
.profiles-users-table {
}

input:focus,
select:focus,
textarea:focus,
button:focus {
    outline: none;
}

.no-data {
    color: var(--v-text-base);
    margin: 20px;
}

.table-row:hover {
    background-color: var(--v-accent-base) !important;
    cursor: pointer;
}

.table-row {
    background-color: white;
}
</style>
